import React, { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { forceReloadContactPlaylists } from '@hooks/API/Contact/UseContactPlaylists';
import { forceReloadPlaylist, forceReloadPlaylistInfo } from '@hooks/API/UsePlaylist';
import { useRemovePlayList } from '@hooks/API/UseWebsites';

import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import {
    buildModelPublicationsSummaryContent,
    buildModelPublicationsSummary,
    getPlaylistPublicationKeys,
} from './modelDetailsBooksPublications.utils';
import ModelDetailsBooksPublicationsDrawer from './ModelDetailsBooksPublicationsDrawer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const SummaryWrapper = styled.div`
    padding: 0.5rem 1rem 0.25rem 1rem;
`;

const SummaryCard = styled.div`
    border: 1px solid ${(props) => props.theme.BorderColor};
    border-radius: 6px;
    background: #fafafa;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 0.875rem 1.25rem;
`;

const SummaryIcon = styled.div`
    flex: 0 0 auto;
    font-size: 1.25rem;
    color: ${(props) => props.theme.TextColorMuted};
    padding-top: 0.15rem;
`;

const SummaryContent = styled.div`
    min-width: 0;
    flex: 1;
    color: ${(props) => props.theme.TextColor};
    font-size: ${(props) => props.theme.FontSizeSm};
`;

const SummaryTitle = styled.div`
    font-size: 0.75rem;
    text-transform: uppercase;
    margin-bottom: 0.2rem;
    color: ${(props) => props.theme.TextColorMuted};
`;

const SummaryLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
    line-height: 1.4;

    &:not(:first-child) {
        margin-top: 0.15rem;
    }
`;

const SummaryWebsiteName = styled.span`
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
`;

const SummaryBullet = styled.span`
    width: 0.35rem;
    height: 0.35rem;
    border-radius: 999px;
    background: ${(props) => props.theme.TextColorMuted};
    flex: 0 0 auto;
    align-self: center;
`;

const SummaryLineText = styled.span`
    min-width: 0;
`;

const SummaryCounterText = styled.span`
    min-width: 0;
`;

const SummaryActions = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
`;

const ModelDetailsBooksPublicationsSummary = ({ playlists = [], readOnly = false, websiteData = [] }) => {
    const { mutateAsync: onRemovePlayList } = useRemovePlayList();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [removingPublicationKey, setRemovingPublicationKey] = useState(null);
    const [hiddenPublicationKeys, setHiddenPublicationKeys] = useState([]);

    const availablePublicationKeys = useMemo(() => getPlaylistPublicationKeys(playlists), [playlists]);
    const hiddenPublicationKeySet = useMemo(() => new Set(hiddenPublicationKeys), [hiddenPublicationKeys]);

    useEffect(() => {
        const availableKeysSet = new Set(availablePublicationKeys);

        // Drops local removals once the refetched playlists no longer expose those publications.
        setHiddenPublicationKeys((currentHiddenKeys) => {
            const nextHiddenKeys = currentHiddenKeys.filter((key) => availableKeysSet.has(key));

            return nextHiddenKeys.length === currentHiddenKeys.length ? currentHiddenKeys : nextHiddenKeys;
        });
    }, [availablePublicationKeys]);

    const publicationsSummary = useMemo(
        () => buildModelPublicationsSummary(playlists, websiteData, hiddenPublicationKeySet),
        [hiddenPublicationKeySet, playlists, websiteData]
    );
    const publicationsSummaryContent = useMemo(
        () => buildModelPublicationsSummaryContent(publicationsSummary, websiteData.length),
        [publicationsSummary, websiteData.length]
    );

    const handleRemovePublication = async (item) => {
        const publicationKey = item.key;
        setRemovingPublicationKey(publicationKey);

        try {
            await onRemovePlayList({
                WebsiteCode: item.WebsiteCode,
                PlaylistID: item.Playlist?.ID,
                DivisionCode: item.DivisionCode,
            });

            setHiddenPublicationKeys((currentHiddenKeys) =>
                currentHiddenKeys.includes(publicationKey) ? currentHiddenKeys : [...currentHiddenKeys, publicationKey]
            );

            if (item.Playlist?.ContactID) {
                forceReloadContact(item.Playlist.ContactID);
                forceReloadContactPlaylists(item.Playlist.ContactID);
            }

            if (item.Playlist?.ID) {
                forceReloadPlaylist(item.Playlist.ID);
                forceReloadPlaylistInfo(item.Playlist.ID);
            }

            return true;
        } catch (error) {
            console.error('error removing playlist publication from summary', error);
            return false;
        } finally {
            setRemovingPublicationKey(null);
        }
    };

    return (
        <SummaryWrapper>
            <SummaryCard>
                <SummaryIcon>
                    <NwIcon icon={byPrefixAndName.fasl['globe']} />
                </SummaryIcon>
                <SummaryContent>
                    <SummaryTitle>{publicationsSummaryContent.title}</SummaryTitle>
                    {publicationsSummaryContent.lines.map((line, index) => (
                        <SummaryLine key={`${line.websiteName}-${index}`}>
                            {line.websiteName ? (
                                <>
                                    <SummaryWebsiteName>{line.websiteName}</SummaryWebsiteName>
                                    <SummaryBullet />
                                </>
                            ) : null}
                            <SummaryLineText>{line.detailsText}</SummaryLineText>
                            {line.counterText ? (
                                <>
                                    <SummaryBullet />
                                    <SummaryCounterText>{line.counterText}</SummaryCounterText>
                                </>
                            ) : null}
                        </SummaryLine>
                    ))}
                </SummaryContent>
                <SummaryActions>
                    {publicationsSummary.length > 0 && (
                        <NwButton
                            minimal
                            icon={byPrefixAndName.fass['expand-wide']}
                            onClick={() => setIsDrawerOpen(true)}
                        />
                    )}
                </SummaryActions>
            </SummaryCard>
            {isDrawerOpen && (
                <ModelDetailsBooksPublicationsDrawer
                    contactID={playlists?.[0]?.ContactID}
                    excludedPublicationKeys={hiddenPublicationKeys}
                    onClose={() => setIsDrawerOpen(false)}
                    onRemovePublication={handleRemovePublication}
                    playlists={playlists}
                    readOnly={readOnly}
                    removingPublicationKey={removingPublicationKey}
                    websiteData={websiteData}
                />
            )}
        </SummaryWrapper>
    );
};

export default ModelDetailsBooksPublicationsSummary;
